import { useState } from "react";
import { Send, CheckCircle2, Phone, MessageCircle, Mail, MapPin } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";
import { useSite, useBrand } from "@/context/SiteContext";
import { useSeo } from "@/lib/seo";
import { REQUIREMENT_OPTIONS } from "@/data/content";

export default function Contact() {
  const { config, waLink, telLink } = useSite();
  const brand = useBrand();
  const [form, setForm] = useState({});
  const [state, setState] = useState({ loading: false, done: false, err: "" });
  useSeo({ title: `Contact Us | ${brand.name}`, description: "Share your solar, inverter, hybrid, battery, UPS, ESS, EV charger or EPC requirement and our team will help you identify the right solution.", canonical: `https://${brand.domain}/contact` });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) { setState({ ...state, err: "Name and Mobile are required." }); return; }
    setState({ loading: true, done: false, err: "" });
    try {
      await api.post("/leads", { ...form, source: "Contact Page" });
      setState({ loading: false, done: true, err: "" });
    } catch (e2) {
      setState({ loading: false, done: false, err: formatApiError(e2.response?.data?.detail) });
    }
  };

  const val = (k, f) => (config && config[k]) || f;

  return (
    <div>
      <PageHeader label="Contact" title="Let's Build Your Energy Solution" sub="Looking for the right solar inverter, hybrid system, battery, UPS, ESS, EV charger or complete solar project solution? Share your requirement and our team will help you identify the right solution." />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 card-surface p-6 sm:p-8">
          {state.done ? (
            <div className="text-center py-12" data-testid="contact-success">
              <CheckCircle2 className="mx-auto text-emerald-400 mb-4" size={56} />
              <h3 className="font-display text-2xl font-bold text-white">Message Received</h3>
              <p className="text-slate-400 mt-2">Thank you for reaching out. Our team will contact you shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
              <input data-testid="contact-form-name" placeholder="Name *" onChange={set("name")} className="cf" />
              <input data-testid="contact-form-company" placeholder="Company Name" onChange={set("company")} className="cf" />
              <input data-testid="contact-form-mobile" placeholder="Mobile Number *" onChange={set("mobile")} className="cf" />
              <input data-testid="contact-form-email" placeholder="Email" onChange={set("email")} className="cf" />
              <input data-testid="contact-form-city" placeholder="City" onChange={set("city")} className="cf" />
              <select data-testid="contact-form-requirement" onChange={set("requirement")} className="cf" defaultValue="">
                <option value="" disabled>Requirement</option>
                {REQUIREMENT_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <input data-testid="contact-form-capacity" placeholder="System Capacity" onChange={set("capacity")} className="cf sm:col-span-2" />
              <textarea data-testid="contact-form-message" placeholder="Message" rows={4} onChange={set("message")} className="cf sm:col-span-2 resize-none" />
              {state.err && <div className="text-red-400 text-sm sm:col-span-2" data-testid="contact-form-error">{state.err}</div>}
              <button type="submit" disabled={state.loading} className="btn-primary sm:col-span-2" data-testid="contact-form-submit"><Send size={16} /> {state.loading ? "Sending..." : "Send Requirement"}</button>
            </form>
          )}
        </div>
        <aside className="space-y-4">
          <div className="card-surface p-6 space-y-4 text-sm">
            <div className="flex items-start gap-3"><MapPin className="text-emerald-400 shrink-0" size={18} /><div><div className="text-slate-500">Address</div><div className="text-slate-200">{val("address", "[ENTER ACTUAL ADDRESS]")}</div></div></div>
            <div className="flex items-start gap-3"><Phone className="text-emerald-400 shrink-0" size={18} /><div><div className="text-slate-500">Phone</div><div className="text-slate-200">{val("phone", "[ENTER ACTUAL PHONE NUMBER]")}</div></div></div>
            <div className="flex items-start gap-3"><Mail className="text-emerald-400 shrink-0" size={18} /><div><div className="text-slate-500">Email</div><div className="text-slate-200">{val("email", "[ENTER ACTUAL EMAIL]")}</div></div></div>
          </div>
          <a href={waLink()} target="_blank" rel="noreferrer" className="btn-primary w-full" data-testid="contact-whatsapp-button"><MessageCircle size={16} /> WhatsApp Us</a>
          <a href={telLink} className="btn-ghost w-full" data-testid="contact-call-button"><Phone size={16} /> Call Now</a>
        </aside>
      </section>
      <style>{`.cf{width:100%;background:#060B11;border:1px solid rgba(255,255,255,.1);border-radius:.6rem;padding:.8rem 1rem;color:#E2E8F0;font-size:.9rem;outline:none}.cf:focus{border-color:#10B981}`}</style>
    </div>
  );
}
